import React, { useCallback, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { TableActions } from "../../../components/tables/tableContext/TableContext";
import { dateForDatabase } from "../../../utils/dateAndTime";
import {
  getAwbStatuses,
  getClients,
  getOversizedPackages,
  getWorkingRoutesNodes,
} from "../../../redux/api/expedition.api";
import { fetchRoutesNomenclatures } from "../../../redux/api/transport.api";
import {
  getOversizedPackagesTableData,
  oversizedPackagesTableHeader,
} from "./components/tableConfigs/oversizedPackagesTableConfig";
import TableWidget from "../../../components/tables/TableWidget";
import { clearOversizedPackages } from "../../../redux/reducer/expedition.reducer";
import OversizedPackagesActionButtons from "./components/ActionButtons/OversizedPackagesActionButtons";

function OversizedPackages() {
  const dispatch = useAppDispatch();
  const {
    expedition: {
      ovsersizedPackages: {
        data: { data: ovsersizedPackagesData, total: totalOversizedPackages },
        isLoading: isLoadingOversizedPackages,
      },
      clients: { data: clientsData },
      awbStatuses: { data: awbStatusesData, isLoading: isLoadingAwbStatus },

      workingRoutesNodes: {
        data: { data: clientsNodesData },
      },
    },
    transport: {
      routesNomenclatures: {
        data: { data: routesNomenclatures },
        isLoading: isLoadingRoutesNomenclatures,
      },
    },
  } = useAppSelector((state) => ({
    expedition: state.expedition,
    transport: state.transport,
  }));

  useEffect(() => {
    dispatch(getAwbStatuses());

    dispatch(
      fetchRoutesNomenclatures({
        perPage: 100,
      }),
    );

    dispatch(
      getWorkingRoutesNodes({
        page: 1,
        perPage: 50,
      }),
    );

    dispatch(getClients({ page: 1, perPage: 99999 }));

    return () => {
      dispatch(clearOversizedPackages());
    };
  }, [dispatch]);

  const getOversizedPackagesHeaderNew = useCallback(
    (initialTableData: any[]) => {
      return initialTableData.map((item) => {
        if (item.value === "nodDestinatie") {
          const nodesDropdown = clientsNodesData?.map(
            ({ nodNume, id_nod }) => ({
              label: nodNume,
              value: id_nod,
            }),
          );
          return {
            withFilter: [{ label: "Toate", value: 0 }, ...nodesDropdown],
            ...item,
          };
        }
        if (item.value === "client") {
          const clientsDropdown = clientsData?.data.map(
            ({ denumireClient, idClient }) => ({
              label: denumireClient,
              value: idClient || 0,
            }),
          );
          return {
            withFilter: [{ label: "Toate", value: "all" }, ...clientsDropdown],
            ...item,
          };
        }
        if (!isLoadingAwbStatus && item.value === "status") {
          const awbStatusesDropdown = awbStatusesData?.map(
            ({ denumire, valoare }) => ({
              label: denumire,
              value: valoare,
            }),
          );
          return {
            withFilter: [
              { label: "Toate", value: "all" },
              ...awbStatusesDropdown,
            ],
            ...item,
          };
        }
        if (!isLoadingRoutesNomenclatures && item.value === "route") {
          const routesDropdown = routesNomenclatures?.map(
            ({ denumire, id_ruta }) => ({
              label: denumire,
              value: id_ruta,
            }),
          );
          return {
            withFilter: [{ label: "Toate", value: 0 }, ...routesDropdown],
            ...item,
          };
        }

        return item;
      });
    },
    [
      awbStatusesData,
      clientsData?.data,
      clientsNodesData,
      isLoadingAwbStatus,
      isLoadingRoutesNomenclatures,
      routesNomenclatures,
    ],
  );

  const handleAwbTableActions = (action: TableActions) => {
    const formatDate = dateForDatabase(action.tableSearch.data_crearii);

    dispatch(
      getOversizedPackages({
        page: action.tablePagination.page,
        perPage: action.tablePagination.rowsPerPage,
        id_client: action.tableSearch.client,
        pctlivrare: action.tableSearch.pctlivrare,
        nodDestinatie: action.tableSearch.nodDestinatie,
        status: action.tableSearch.status,
        awb: action.tableSearch.awb,
        route: action.tableSearch.route,
        data_crearii: formatDate,
      }),
    );
  };

  const oversizedPackagesTableHeaderNew = useMemo(
    () => getOversizedPackagesHeaderNew(oversizedPackagesTableHeader),
    [getOversizedPackagesHeaderNew],
  );

  return (
    <>
      <TableWidget
        tableTitle="Colete agabaritice"
        tableHeaderData={oversizedPackagesTableHeaderNew}
        tableItemsData={getOversizedPackagesTableData(ovsersizedPackagesData)}
        totalItems={totalOversizedPackages}
        tableLoading={isLoadingOversizedPackages}
        handleTableActions={handleAwbTableActions}
        ActionButtonsComponent={(props: any) => (
          <OversizedPackagesActionButtons {...props} />
        )}
        borderedRow
      />
    </>
  );
}

export default OversizedPackages;
